import { forwardRef, useState } from "react";
import Select from "react-select";
import { Button, Form, Alert, ButtonGroup } from "react-bootstrap";

import { PersonPicker } from "./PersonPicker";
import { AccountEditor } from "./AccountEditor";
import { getCSRFToken } from "../utils/csrf";

const readJson = (id) => {
  const el = document.getElementById(id);
  return el ? JSON.parse(el.textContent) : [];
};

const currentYear = new Date().getFullYear();

export const InscripcionForm = forwardRef((props, rootRef) => {
  const ds = rootRef.current.dataset;
  const [urls] = useState(() => ({
    buscarPersona: ds.buscarPersonaUrl,
    personaCuentas: ds.personaCuentasUrl,
    cuenta: ds.cuentaUrl,
    cuentaGuardar: ds.cuentaGuardarUrl,
    crear: ds.crearUrl,
  }));
  const [cursoOptions] = useState(() =>
    readJson("inscripcion-cursos").map((c) => ({ value: c.id, label: c.descripcion }))
  );
  const [servicioOptions] = useState(() =>
    readJson("inscripcion-servicios").map((s) => ({ value: s.id, label: s.descripcion }))
  );
  const [docTipoOptions] = useState(() =>
    readJson("inscripcion-doc-tipos").map((d) => ({ value: d.id, label: d.descripcion }))
  );
  const showDivision = ds.showDivision === "true";

  // Por defecto se abre en el buscador de cuentas — crear una familia nueva es la
  // excepción, no lo primero que un directivo hace al entrar a esta página.
  const [mode, setMode] = useState("editar");

  const [cursoId, setCursoId] = useState(null);
  const [anio, setAnio] = useState(currentYear);
  const [division, setDivision] = useState("A");
  const [servicios, setServicios] = useState([]);
  const [alumno, setAlumno] = useState(null);
  const [padre1, setPadre1] = useState(null);
  const [padre2Enabled, setPadre2Enabled] = useState(false);
  const [padre2, setPadre2] = useState(null);
  const [contactoIndex, setContactoIndex] = useState(0);

  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const resetForm = () => {
    setCursoId(null);
    setAnio(currentYear);
    setDivision("A");
    setServicios([]);
    setAlumno(null);
    setPadre1(null);
    setPadre2Enabled(false);
    setPadre2(null);
    setContactoIndex(0);
    setErrors({});
  };

  const handleCrear = async () => {
    setSubmitting(true);
    setErrors({});

    const padres = [padre1, ...(padre2Enabled && padre2 ? [padre2] : [])].filter(Boolean);

    try {
      const res = await fetch(urls.crear, {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-CSRFToken": getCSRFToken() },
        body: JSON.stringify({
          curso_id: cursoId,
          anio,
          division: showDivision ? division : undefined,
          alumno: alumno || {},
          servicios,
          padres,
          contacto_index: padre2Enabled && padre2 ? contactoIndex : 0,
        }),
      });
      const data = await res.json();
      if (data.ok) {
        window.location = data.redirect_url;
      } else {
        setErrors(data.errors || {});
      }
    } catch (e) {
      setErrors({ non_field: "Error de red — intentá de nuevo." });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="card">
      <div className="card-header d-flex align-items-center justify-content-between">
        <h3 className="title mb-0">Inscripción</h3>
        <ButtonGroup>
          <Button
            variant={mode === "editar" ? "primary" : "outline-primary"}
            onClick={() => setMode("editar")}
          >
            Buscar cuenta existente
          </Button>
          <Button
            variant={mode === "crear" ? "primary" : "outline-primary"}
            onClick={() => setMode("crear")}
          >
            + Nueva inscripción
          </Button>
        </ButtonGroup>
      </div>

      <div className="card-body">
        {mode === "editar" ? (
          <AccountEditor
            urls={urls}
            cursoOptions={cursoOptions}
            servicioOptions={servicioOptions}
            docTipoOptions={docTipoOptions}
          />
        ) : (
          <>
            {errors.non_field && <Alert variant="danger">{errors.non_field}</Alert>}

            <h5>Curso</h5>
            <div className="row mb-3">
              <div className="col-md-6">
                <label className="form-label">Curso *</label>
                <Select
                  options={cursoOptions}
                  value={cursoOptions.find((o) => o.value === cursoId) || null}
                  onChange={(o) => setCursoId(o ? o.value : null)}
                  placeholder="Elegir curso…"
                />
                {errors.curso && <div className="text-danger small mt-1">{errors.curso}</div>}
              </div>
              <div className="col-md-3">
                <label className="form-label">Año lectivo *</label>
                <Form.Control
                  type="number"
                  value={anio}
                  onChange={(e) => setAnio(parseInt(e.target.value, 10) || "")}
                />
                {errors.anio && <div className="text-danger small mt-1">{errors.anio}</div>}
              </div>
              {showDivision && (
                <div className="col-md-3">
                  <label className="form-label">División</label>
                  <Form.Select value={division} onChange={(e) => setDivision(e.target.value)}>
                    <option value="A">A</option>
                    <option value="B">B</option>
                    <option value="C">C</option>
                    <option value="D">D</option>
                  </Form.Select>
                </div>
              )}
            </div>

            <h5>Alumno</h5>
            <PersonPicker
              value={alumno}
              onChange={setAlumno}
              buscarUrl={urls.buscarPersona}
              showFecNac
              required
              error={errors.alumno}
            />

            <h5>Servicios / cuotas</h5>
            <div className="mb-3">
              <Select
                isMulti
                options={servicioOptions}
                value={servicioOptions.filter((o) => servicios.includes(o.value))}
                onChange={(opts) => setServicios((opts || []).map((o) => o.value))}
                placeholder="Elegir servicios…"
              />
              {errors.servicios && <div className="text-danger small mt-1">{errors.servicios}</div>}
            </div>

            <h5>Padre / Madre 1</h5>
            <PersonPicker
              value={padre1}
              onChange={setPadre1}
              buscarUrl={urls.buscarPersona}
              required
              error={errors.padre1}
            />
            {padre2Enabled && padre2 && (
              <Form.Check
                type="radio"
                name="contacto"
                label="Responsable de facturación"
                checked={contactoIndex === 0}
                onChange={() => setContactoIndex(0)}
              />
            )}

            {padre2Enabled ? (
              <>
                <h5 className="mt-3">Padre / Madre 2</h5>
                <PersonPicker
                  value={padre2}
                  onChange={setPadre2}
                  buscarUrl={urls.buscarPersona}
                  error={errors.padre2}
                />
                {padre2 && (
                  <Form.Check
                    type="radio"
                    name="contacto"
                    label="Responsable de facturación"
                    checked={contactoIndex === 1}
                    onChange={() => setContactoIndex(1)}
                  />
                )}
              </>
            ) : (
              <Button variant="link" className="ps-0" onClick={() => setPadre2Enabled(true)}>
                + Agregar segundo padre/madre
              </Button>
            )}

            <div className="mt-4 mb-2">
              <Button variant="primary" disabled={submitting} onClick={handleCrear}>
                {submitting ? "Creando…" : "Crear inscripción"}
              </Button>
              <Button variant="outline-secondary" className="ms-2" onClick={resetForm} disabled={submitting}>
                Limpiar
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
});

export default InscripcionForm;
